import { validateContact } from "../lib/contactValidation";
import { MAX_MESSAGE } from "./composeMessage";
import { INTENT_QUESTIONS } from "./ids";
import type { Draft, FieldErrors } from "./state";

/**
 * Validazione della richiesta guidata. Le regole sui dati di contatto restano
 * quelle condivise (`validateContact`, identiche al backend); qui si aggiungono
 * solo quelle dei passi (argomento, data) e il limite di lunghezza, che il
 * server applicherebbe tagliando in silenzio.
 */

export function validateTopic(d: Draft): FieldErrors {
  return d.intent ? {} : { intent: true };
}

/**
 * «Entro una data» con una data passata: si chiede di correggerla. Solo se la
 * domanda sui tempi riguarda l'intento scelto: una risposta rimasta da un altro
 * intento non parte e quindi non blocca.
 */
export function validateDetails(d: Draft, todayIso: string): FieldErrors {
  const asksTiming = !!d.intent && INTENT_QUESTIONS[d.intent].includes("timing");
  if (asksTiming && d.timing === "date" && d.date && d.date < todayIso) return { date: true };
  return {};
}

export interface FinalInput {
  draft: Draft;
  email: string;
  message: string;
}

/**
 * Tutto ciò che serve per inviare: nome, email e telefono (obbligatorio dal
 * 2026-10-03, come nel backend); la nota solo senza argomento o con «Altro».
 */
export function validateFinal({ draft, email, message }: FinalInput): FieldErrors {
  const base = validateContact({ name: draft.name, email, phone: draft.phone, message });
  const errors: FieldErrors = {};
  if (base.name) errors.name = true;
  if (base.email) errors.email = true;
  if (base.phone) errors.phone = true;
  if (base.message) errors.note = "required";
  else if (message.length > MAX_MESSAGE) errors.note = "tooLong";
  return errors;
}

export type MissingItem = "request" | "name" | "email" | "phone" | "shorter" | "date";

/**
 * La riga «Da completare: …» sotto la cartolina: derivata dalle STESSE regole
 * che bloccano l'invio, così non può dire «pronta» quando l'invio verrebbe
 * rifiutato. Mai il consenso E: è facoltativo e indipendente.
 */
export function missingItems(input: FinalInput & { todayIso: string }): MissingItem[] {
  const e = { ...validateDetails(input.draft, input.todayIso), ...validateFinal(input) };
  const out: MissingItem[] = [];
  if (e.note === "required") out.push("request");
  if (e.note === "tooLong") out.push("shorter");
  if (e.name) out.push("name");
  if (e.email) out.push("email");
  if (e.phone) out.push("phone");
  if (e.date) out.push("date");
  return out;
}

export function hasErrors(e: FieldErrors): boolean {
  return Object.keys(e).length > 0;
}
